import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import DevLandingVideoPlayer from '../components/DevLandingVideoPlayer';
import DevLandingStory from '../components/DevLandingStory';
import { loadMetaPixel, trackMetaEvent, trackPageView } from '../utils/metaPixel';
import '../styles/DevLandingPage.css';

const STORAGE_KEY = 'runamatic_devlanding_v3';

const CALENDAR_URL =
  process.env.REACT_APP_DEV_LANDING_CALENDAR_URL ||
  'https://calendar.google.com/calendar/appointments/schedules/AcZssZ1O_CqIgKoF3SokhLwgDTpm8gQq6J0hGJ2nPQMwrK8dWPeLvKMzbGsxk_2dHMNoGaMwMTdreNPl?gv=true';

const readStorage = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const writeStorage = (data) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* ignore quota / private mode */
  }
};

const DevLandingPage = () => {
  const reduceMotion = useReducedMotion();
  const ctaRef = useRef(null);
  const videoSectionRef = useRef(null);
  const scheduleSentRef = useRef(false);
  const viewContentSentRef = useRef(false);

  const stored = readStorage();
  const [videoWatched, setVideoWatched] = useState(Boolean(stored?.videoWatched));
  const [accepted, setAccepted] = useState(
    Boolean(stored?.accepted) && Boolean(stored?.videoWatched)
  );

  useEffect(() => {
    let active = true;
    loadMetaPixel().then(() => {
      if (active) trackPageView();
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    writeStorage({
      videoWatched,
      accepted: videoWatched ? accepted : false,
      viewContentSent: viewContentSentRef.current,
      scheduleSent: scheduleSentRef.current
    });
  }, [accepted, videoWatched]);

  useEffect(() => {
    if (stored?.scheduleSent) scheduleSentRef.current = true;
    if (stored?.viewContentSent) viewContentSentRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleVideoWatched = () => {
    setVideoWatched(true);
    writeStorage({
      videoWatched: true,
      accepted,
      viewContentSent: viewContentSentRef.current,
      scheduleSent: scheduleSentRef.current
    });
  };

  const handleAcceptChange = (checked) => {
    if (!videoWatched) {
      videoSectionRef.current?.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'center'
      });
      return;
    }

    setAccepted(checked);
    if (checked) {
      if (!viewContentSentRef.current) {
        trackMetaEvent('ViewContent', {
          content_name: 'devlanding_offer',
          content_category: 'custom_software'
        });
        viewContentSentRef.current = true;
      }
      writeStorage({
        videoWatched: true,
        accepted: true,
        viewContentSent: true,
        scheduleSent: scheduleSentRef.current
      });
      window.requestAnimationFrame(() => {
        ctaRef.current?.scrollIntoView({
          behavior: reduceMotion ? 'auto' : 'smooth',
          block: 'center'
        });
      });
    } else {
      writeStorage({
        videoWatched: true,
        accepted: false,
        viewContentSent: viewContentSentRef.current,
        scheduleSent: scheduleSentRef.current
      });
    }
  };

  const handleReserve = () => {
    if (!accepted || !videoWatched) return;

    if (!scheduleSentRef.current) {
      trackMetaEvent('Schedule', {
        content_name: 'devlanding_reserve_click',
        content_category: 'custom_software'
      });
      scheduleSentRef.current = true;
      writeStorage({
        videoWatched: true,
        accepted: true,
        viewContentSent: viewContentSentRef.current,
        scheduleSent: true
      });
    }

    window.open(CALENDAR_URL, '_blank', 'noopener,noreferrer');
  };

  const motionProps = reduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 18 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] }
      };

  const ctaMotion = reduceMotion
    ? {}
    : {
        initial: false,
        animate: accepted ? { opacity: 1, y: 0 } : { opacity: 0.45, y: 0 },
        transition: { duration: 0.35 }
      };

  return (
    <>
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link rel="preconnect" href="https://cdn.tiendaruna.io" />
        <link
          href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@600;700;800&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <SEO
        title="Software a medida para empresas"
        description="Desarrollo de software 100% a medida. Código fuente opcional, hosting mensual opcional, desde 1000 USD, entrega en 30 días. Cotiza tu proyecto con Runamatic."
        canonical="/devlanding"
        robots="noindex, nofollow"
        ogTitle="Software a medida para empresas | Runamatic"
        ogDescription="Trabajo 100% a medida. Desde 1000 USD. Entrega en 30 días. Cotiza tu proyecto."
      />

      <div className="dl-page">
        <div className="dl-page__noise" aria-hidden="true" />
        <div className="dl-shell">
          <motion.header className="dl-brand" {...motionProps}>
            <p className="dl-brand__name">Runamatic</p>
          </motion.header>

          <motion.section className="dl-hero" aria-labelledby="dl-title" {...motionProps}>
            <h1 id="dl-title" className="dl-h1">
              <span className="dl-h1__line">Software a medida</span>
              <span className="dl-h1__accent">Para empresas</span>
            </h1>

            <p className="dl-lead">
              Nuestro trabajo es <strong>100% a medida</strong> de sus necesidades.
            </p>

            <div ref={videoSectionRef} className="dl-video-wrap">
              <DevLandingVideoPlayer watched={videoWatched} onWatched={handleVideoWatched} />
            </div>

            <DevLandingStory />

            <div className="dl-info">
              <p className="dl-info__heading">Elige cómo operar el software</p>

              <div className="dl-paths" role="list">
                <div className="dl-path" role="listitem">
                  <span className="dl-path__option">Opción 1</span>
                  <p className="dl-path__title">Código fuente y base de datos</p>
                  <p className="dl-path__desc">
                    Se lo entregamos con instrucciones para correrlo por su cuenta.
                  </p>
                </div>
                <p className="dl-paths__or" aria-hidden="true">
                  o
                </p>
                <div className="dl-path" role="listitem">
                  <span className="dl-path__option">Opción 2</span>
                  <p className="dl-path__title">Nosotros lo alojamos</p>
                  <p className="dl-path__desc">Lo operamos por un costo mensual.</p>
                </div>
              </div>

              <p className="dl-note">
                El dominio no está incluido: debe ser propiedad del cliente.
              </p>

              <div className="dl-anchors" role="group" aria-label="Precio y plazo">
                <div className="dl-anchor">
                  <span className="dl-anchor__label">Desde</span>
                  <span className="dl-anchor__value">1000 USD</span>
                </div>
                <div className="dl-anchor">
                  <span className="dl-anchor__label">Entrega</span>
                  <span className="dl-anchor__value">30 días</span>
                </div>
              </div>

              <p className="dl-micro">
                En la reunión cotizamos su proyecto según alcance y prioridades.
              </p>
            </div>
          </motion.section>

          <section
            className={`dl-gate${accepted ? ' dl-gate--on' : ''}${
              !videoWatched ? ' dl-gate--locked' : ''
            }`}
            aria-label="Aceptación de la oferta"
          >
            <p className="dl-gate__eyebrow">Siguiente paso</p>
            {!videoWatched && (
              <p className="dl-gate__lock">
                Primero mirá el video de arriba para habilitar esta opción.
              </p>
            )}
            <label
              className={`dl-check${!videoWatched ? ' dl-check--disabled' : ''}`}
              onClick={(e) => {
                if (!videoWatched) {
                  e.preventDefault();
                  handleAcceptChange(true);
                }
              }}
            >
              <input
                type="checkbox"
                checked={accepted}
                aria-disabled={!videoWatched}
                onChange={(e) => handleAcceptChange(e.target.checked)}
              />
              <span className="dl-check__box" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 14 14" fill="none">
                  <path
                    d="M2.5 7.2L5.4 10.1L11.5 3.8"
                    stroke="#000"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className="dl-check__text">
                Vi el video y me gustaría cotizar mi proyecto
              </span>
            </label>
          </section>

          <motion.section
            ref={ctaRef}
            className="dl-cta"
            aria-label="Reservar reunión"
            {...ctaMotion}
          >
            <button
              type="button"
              className="dl-btn dl-btn--primary"
              onClick={handleReserve}
              disabled={!accepted || !videoWatched}
            >
              Reservar reunión
            </button>
            <p className="dl-cta__hint">
              Elige un horario disponible. Completarás tus datos al confirmar la reserva.
            </p>
          </motion.section>

          <p className="dl-privacy">
            Al continuar aceptas nuestra{' '}
            <Link to="/privacy-policy">política de privacidad</Link>.
          </p>
        </div>
      </div>
    </>
  );
};

export default DevLandingPage;
